// Dependency-free JSON schema subset shared by browser and server validators.
// CommonJS files under client/src must not use object spread or rest: Babel imports its helper as an ES module,
// webpack then treats the file as ESM and module.exports throws at load (blank site, 2026-10-24). Use Object.assign.
const UUID_PATTERN = '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$';
const object = (v) => v !== null && typeof v === 'object' && [Object.prototype, null].includes(Object.getPrototypeOf(v));
const own = (v, k) => Object.prototype.hasOwnProperty.call(v, k);
function freeze(v) {
  if (v && typeof v === 'object') { Object.values(v).forEach(freeze); Object.freeze(v); }
  return v;
}
const text = { type: 'string', minLength: 1 };
const uuid = { type: 'string', pattern: UUID_PATTERN };
const number = (min, max) => ({ type: 'number', minimum: min, maximum: max });
const integer = (min, max) => Object.assign(number(min, max), { type: 'integer' });
const enumeration = (...values) => ({ enum: values });
const nullable = (schema) => ({ anyOf: [schema, { type: 'null' }] });
const array = (items, extra = {}) => Object.assign({ type: 'array', items }, extra);
const shape = (properties, required = Object.keys(properties)) => ({ type: 'object', properties, required, additionalProperties: false });
const record = (values, keys) => Object.assign({ type: 'object', additionalProperties: values }, keys ? { propertyNames: keys } : {});
function check(schema, value, field = '', errors = []) {
  const fail = (reason) => errors.push({ code: 'INVALID_VALUE', field, reason });
  if (schema.anyOf) {
    if (!schema.anyOf.some((v) => check(v, value, field, []).length === 0)) fail('type_or_value');
    return errors;
  }
  if (schema.enum && !schema.enum.includes(value)) { fail('enum'); return errors; }
  const type = schema.type;
  const valid = !type || (type === 'null' ? value === null : type === 'object' ? object(value)
    : type === 'array' ? Array.isArray(value) : type === 'integer' ? Number.isSafeInteger(value)
      : type === 'number' ? typeof value === 'number' && Number.isFinite(value) : typeof value === type);
  if (!valid) { fail('type'); return errors; }
  if (typeof value === 'number') {
    if (schema.minimum !== undefined && value < schema.minimum) fail('minimum');
    if (schema.maximum !== undefined && value > schema.maximum) fail('maximum');
  }
  if (typeof value === 'string') {
    if (schema.minLength && value.trim().length < schema.minLength) fail('min_length');
    if (schema.maxLength && value.length > schema.maxLength) fail('max_length');
    if (schema.pattern && !new RegExp(schema.pattern).test(value)) fail('pattern');
  }
  if (Array.isArray(value)) {
    if (schema.minItems && value.length < schema.minItems) fail('min_items');
    if (schema.maxItems !== undefined && value.length > schema.maxItems) fail('max_items');
    if (schema.uniqueItems && new Set(value.map((v) => JSON.stringify(v))).size !== value.length) fail('duplicate');
    value.forEach((v, i) => check(schema.items, v, `${field}[${i}]`, errors));
  }
  if (object(value)) {
    (schema.required || []).forEach((key) => {
      if (!own(value, key)) errors.push({ code: 'REQUIRED_FIELD', field: field ? `${field}.${key}` : key });
    });
    Object.keys(value).forEach((key) => {
      const path = field ? `${field}.${key}` : key;
      const properties = schema.properties || {};
      if (schema.propertyNames) check(schema.propertyNames, key, path, errors);
      if (own(properties, key)) check(properties[key], value[key], path, errors);
      else if (schema.additionalProperties && typeof schema.additionalProperties === 'object') check(schema.additionalProperties, value[key], path, errors);
      else if (schema.additionalProperties === false) errors.push({ code: 'INVALID_VALUE', field: path, reason: 'unknown_key' });
    });
  }
  return errors;
}
function result(value, errors) {
  return { ok: errors.length === 0, errors, value: errors.length ? null : JSON.parse(JSON.stringify(value)) };
}
module.exports = { UUID_PATTERN, object, own, freeze, text, uuid, number, integer, enumeration, nullable, array, shape, record, check, result };
